import { useState } from "preact/hooks";
import { feel } from "../feel/kit.js";
import { AT, useCount, useMoment } from "../moments/moments.js";
import type { Earned } from "./beans.js";
import { levelOf } from "./beans.js";

/**
 * What a play earned (ADR 0043): "+76 beans", what for, and the level bar, with a word when the
 * level goes up. Shown on every result, solo and in rooms; counted up as a moment (P5-UX-03).
 */
export function BeansEarned({ earned, total }: { earned: Earned; total: number }) {
  const now = levelOf(total);
  const before = levelOf(total - earned.total);
  const up = now.level > before.level;
  // Moments (P5-UX-03): the beans count up and the bar fills, then a level up has its moment.
  const counted = useCount(0, earned.total, AT.beans);
  const filled = useCount(up ? 0 : before.into, now.into, AT.beans);
  const [levelShown, setLevelShown] = useState(false);
  useMoment(
    up
      ? [
          {
            at: AT.level,
            run: () => {
              setLevelShown(true);
              feel("levelUp");
            },
          },
        ]
      : [],
  );
  if (earned.total <= 0) return null;
  return (
    <div class="beans-earned" aria-live="polite">
      <p class="beans-total">
        {/* Screen readers hear the sum, not the count; once counted, it's the one line. */}
        {counted < earned.total ? (
          <strong>
            <span aria-hidden="true">+{counted} beans</span>
            <span class="visually-hidden">+{earned.total} beans</span>
          </strong>
        ) : (
          <strong>+{earned.total} beans</strong>
        )}
        {up && levelShown && <span class="level-up"> Level {now.level}!</span>}
      </p>
      <ul class="beans-parts">
        {earned.parts.map((p) => (
          <li key={p.label}>
            {p.label} <span>+{p.beans}</span>
          </li>
        ))}
      </ul>
      <div class="beans-level">
        <span>
          Level {now.level} · {now.need - now.into} to level {now.level + 1}
        </span>
        <progress max={now.need} value={filled} aria-hidden="true" />
      </div>
    </div>
  );
}
